<script lang="ts">
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import ProductContainerDetails from "@shared/filters/product_container_details.js";
  import CartProductDealDescription from "@shared/svelte/Components/Deal/CartProductDealDescription.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import ContainerDepositInfo from "@retailer/svelte/Shared/ContainerDepositInfo.svelte";
  import type { CartProduct } from "@shared/types/ProductLine";
  import { get as getStoreValue } from "svelte/store";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import ProductHelpers from "@shared/svelte/ProductHelpers.js";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Reorder from "@retailer/svelte/OrderDetail/Reorder.svelte";
  import type { Distributor } from "@shared/types/Distributor";

  export let product: CartProduct;
  export let excludedProduct: boolean;
  export let Product;
  export let distributor: Distributor;
  export let updatedOrder: boolean;

  const stateSettings = getStoreValue(StateSettingsStore);

  function replacementForString(product) {
    let text = [];
    if (product.original_case_quantity) {
      let container = product.original_case_quantity > 1 ? " cases" : " case";
      text.push(product.original_case_quantity + container);
    }
    if (product.original_unit_quantity) {
      let container =
        product.original_unit_quantity > 1
          ? Product.pluralContainerType(product.container_type)
          : product.container_type;
      text.push(product.original_unit_quantity + " " + container);
    }
    return text.join(", ");
  }

  function unitQuantityReplaced(product) {
    return (
      product.original_unit_quantity != null &&
      product.original_unit_quantity != product.unit_quantity
    );
  }

  function caseQuantityReplaced(product) {
    return (
      product.original_case_quantity != null &&
      product.original_case_quantity != product.case_quantity
    );
  }

  function showReplacementForBox(product) {
    return (
      updatedOrder &&
      !excludedProduct &&
      (unitQuantityReplaced(product) || caseQuantityReplaced(product))
    );
  }

  function unitQuantity(product) {
    return updatedOrder ? product.unit_quantity : product.original_unit_quantity;
  }

  function caseQuantity(product) {
    return updatedOrder ? product.case_quantity : product.original_case_quantity;
  }

  function total() {
    if (!updatedOrder && product.original_total) {
      return product.original_total;
    } else {
      return product.total;
    }
  }

  function subtotal() {
    if (!updatedOrder && product.original_subtotal) {
      return product.original_subtotal;
    } else {
      return product.subtotal;
    }
  }

  const showSkuIfAvailable = stagedRolloutCheck("show_sku_on_pdp");
</script>

<style>
  .reorder-container {
    min-width: 272px;
  }
</style>

<div class="fizz-inset-stretch-24 order-detail__order-item">
  <div class="fizz-flex-row-8 fizz-align-content-start">
    <UIRouterLink
      stateName="layout.product_detail"
      params={{ productId: product.product_line.id }}
      style="min-width: 80px;"
    >
      <ProductImage
        product={{ cloudinary_image_url: product.product_line.cloudinary_image_url }}
        fizz={true}
        height={$LayoutStore.isDesktop ? 80 : 60}
        width={$LayoutStore.isDesktop ? 80 : 60}
        className={excludedProduct ? "opacity-40" : ""}
      />
    </UIRouterLink>
    <div class="fizz-flex-row-responsive fizz-full-width fizz-space-between">
      <UIRouterLink
        stateName="layout.product_detail"
        classes="nounderline-hover fizz-text-inherit"
        params={{ productId: product.product_line.id }}
      >
        <p class="fizz-text-large no-translate">
          {product.product_line.name}
        </p>
        <div class="fizz-text-subdued" class:opacity-40={excludedProduct}>
          <span class="fizz-text-strong">Size:</span>
          {ProductContainerDetails()(product, null, null, true)}
        </div>
        {#if showSkuIfAvailable && product.sku}
          <div class="fizz-text-small fizz-text-subdued">
            SKU: {product.sku}
          </div>
        {/if}
        {#if product.container_deposit}
          <ContainerDepositInfo
            containerDeposit={product.container_deposit}
            containerType={product.container_type}
            containerCount={ProductHelpers.orderProductUnitCount(product)}
            includedInPrice={product.deposit_included_in_price}
          />
        {/if}
        {#if product.discounts.length > 0 && !stateSettings["uses_future_pricing_with_min_bottle"]}
          <CartProductDealDescription cartProduct={product} showNextTier={false} />
        {/if}
      </UIRouterLink>
      <div class="fizz-stack-8 fizz-align-self-start">
        {#if !excludedProduct}
          <div class="fizz-text-subdued fizz-text-strong" data-testid="product-quantity">
            QTY: {caseQuantity(product)} cases, {unitQuantity(product)}
            {Product.pluralContainerType(product.container_type)}
          </div>
          <div
            class="fizz-styles fizz-text-small fizz-text-strong"
            class:fizz-stack-12={showReplacementForBox(product)}
            data-testid="product-total"
          >
            {#if product.has_custom_price}<span>Custom Price: </span>
            {:else}
              <span>Price: </span>
            {/if}
            {#if product.discount_amount > 0}
              <span class="strikethrough color-black">
                <FormatMoney input={subtotal()} />
              </span>
            {/if}
            <span
              class:fizz-text-deal={product.discount_amount > 0}
              class:color-black={product.discount_amount === 0}
              class="price-string"
            >
              <FormatMoney input={total()} />
            </span>
          </div>
        {/if}
        {#if updatedOrder}
          {#if product.out_of_stock}
            <p class="fizz-text fizz-text-strong fizz-text-negative">Marked Out of Stock</p>
          {/if}
          {#if product.removed_by_rep}
            <p class="fizz-text fizz-text-strong fizz-text-negative">Product Removed</p>
          {/if}
          {#if product.backordered}
            <p class="fizz-text fizz-text-strong fizz-text-negative">Back Ordered</p>
          {/if}
          {#if product.requested_item}
            <p class="fizz-text fizz-text-strong fizz-text-negative">Request Rejected</p>
          {/if}
        {/if}
        {#if showReplacementForBox(product)}
          <div class="fizz-styles fizz-text-medium">
            <div class="fizz-surface-gray fizz-inset-square-16">
              <h4 class="fizz-text-medium"><strong>Replacement for:</strong></h4>
              <p>{product.product_line.name} - {ProductContainerDetails()(product)}</p>
              <p>
                {replacementForString(product)}
              </p>
            </div>
          </div>
        {/if}
      </div>
      {#if product.deal_notes || (product.permanent_deal_notes && product.permanent_deal_notes.notes)}
        <div class="fizz-flex-row-4" class:opacity-40={excludedProduct}>
          <Icon type="DealIcon" height={12} width={12} />
          <p class="fizz-text-small fizz-text-strong">
            Deal Note:
            <span class="fizz-text-regular">
              {`"${product.deal_notes || product.permanent_deal_notes.notes}"`}
            </span>
          </p>
        </div>
      {/if}
    </div>
  </div>
  {#if !stateSettings["uses_future_pricing_with_min_bottle"]}
    <div class="fizz-flex-row fizz-justify-end">
      <div
        class="fizz-full-width"
        class:fizz-half-width={$LayoutStore.isDesktop}
        class:reorder-container={$LayoutStore.isDesktop}
      >
        <Reorder {product} productLine={product.product_line} {distributor} />
      </div>
    </div>
  {/if}
</div>
